import { UseFormReturn } from '@graphcommerce/ecommerce-ui'
import {
  CustomizableDropDownValue,
  CustomizableProductInterface,
} from '@graphcommerce/graphql-mesh'
import { Money } from '@graphcommerce/magento-store'
import { filterNonNullableKeys } from '@graphcommerce/next-ui'
import { MenuItem } from '@mui/material'
import React, { useCallback, useEffect, useMemo } from 'react'
import { StandardDropdown } from '../../Dropdown/StandardDropdown'
import { OptionTypeRenderer } from '../../Product/ProductCustomizable/ProductCustomizable'

export const TurnAroundTimeDropDownOption = React.memo<
  React.ComponentProps<OptionTypeRenderer['CustomizableDropDownOption']> & {
    form: UseFormReturn<any>
    selectedVariantSku?: string | null
    product: Pick<CustomizableProductInterface, 'options'>
    colorSpecOptionValue?: CustomizableDropDownValue
  }
>((props) => {
  const { uid, required, optionIndex, index, title, dropdownValue, form, colorSpecOptionValue } =
    props
  const { setValue, getFieldState } = form

  const { watch } = form

  const enteredOptions = watch('enteredOptions')
  const quantity = watch('quantity')

  const allowedOptions = useMemo(() => {
    if (!dropdownValue || !enteredOptions) {
      return []
    }

    return dropdownValue
      .filter((v) => {
        const value = v as CustomizableDropDownValue

        if (
          !value.fourover_data?.conditions?.colorSpec ||
          !colorSpecOptionValue ||
          // We keep runsize with quantity, so we need to split before compare
          colorSpecOptionValue?.title?.split(' - ').at(0) !==
            value.fourover_data?.conditions?.colorSpec
        ) {
          return false
        }

        if (
          !value.fourover_data?.conditions?.runSize ||
          !quantity ||
          quantity !== value.fourover_data?.conditions?.runSize
        ) {
          return false
        }
        return true
      })
      .map((v) => {
        const value = v as CustomizableDropDownValue
        if (!value.title?.includes('Business Day') && value.title?.includes('Day')) {
          const dayParts = value.title.split(' ')
          const dayValue = dayParts?.at(0)
          return { ...value, title: `${dayValue} Business Days` }
        }
        return value
      })
  }, [dropdownValue, enteredOptions, quantity, colorSpecOptionValue])

  const handleOptionSelect = useCallback(
    (newValue: string) => {
      const entered = Array.isArray(enteredOptions)
        ? [...enteredOptions].filter((item) => item)
        : []
      if (entered.find((o) => o.uid === uid)) {
        entered.find((o) => o.uid === uid).value = newValue
      } else {
        entered.push({ uid, value: newValue })
      }
      setValue(`enteredOptions`, entered, {})
    },
    [enteredOptions, uid, setValue],
  )

  const selectedValue = enteredOptions ? enteredOptions.find((o) => o?.uid === uid)?.value : ''

  useEffect(() => {
    // Auto-select the first option
    const selectedOption = allowedOptions?.find((o) => o && o?.uid?.toString() === selectedValue)
    if (!selectedOption) {
      const cheapest = allowedOptions.length
        ? allowedOptions.reduce((a, b) => ((a?.price || 0) < (b?.price || 0) ? a : b))
        : null
      if (cheapest) {
        handleOptionSelect(cheapest?.uid?.toString() || '')
      }
    }
  }, [selectedValue, uid, handleOptionSelect, allowedOptions, quantity])

  if (!colorSpecOptionValue) {
    return null
  }

  return (
    <StandardDropdown
      value={selectedValue || ''}
      label={title || ''}
      onChange={(newValue) => {
        if (uid && newValue?.target?.value) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          handleOptionSelect(newValue?.target?.value?.toString())
        }
      }}
      options={filterNonNullableKeys(allowedOptions, ['title']).map((option) => (
        <MenuItem
          value={option.uid?.toString() || ''}
          className='flex w-full items-center justify-between'
        >
          <span>{option.title}</span>
          {!!option.price && option.price > 0 && (
            <>
              +<Money value={option.price} />
            </>
          )}
        </MenuItem>
      ))}
      twoColumns
      fullWidth
      placeholder='Select...'
    />
  )
})
